/**
 * SortableList — reordenar uma lista vertical arrastando, com feedback de
 * onde o item vai cair.
 *
 * Contrato do markup:
 *   - o container leva `phx-hook="SortableList"` e
 *     `data-sortable-event="<nome do evento>"`;
 *   - cada item leva `data-sortable-id="<chave>"`;
 *   - a alça de arrasto é `[data-drag-handle]` DENTRO do item.
 *
 * No drop o hook envia `{order: [...chaves...]}` com `pushEventTo(this.el, ...)`,
 * respeitando o `phx-target` do container (LiveComponent ou LiveView).
 *
 * ## Três decisões que este arquivo carrega
 *
 * **O arraste só começa pela alça.** O item vira `draggable` no `mousedown`
 * da alça e deixa de ser no `mouseup`. Sem isso, o botão e o interruptor que
 * moram na mesma linha começariam um arraste a cada clique.
 *
 * **A marca de destino é uma LINHA, não um contorno.** Um contorno no item
 * de baixo não diz se o arrastado cai antes ou depois dele; a linha aparece
 * na borda de cima ou de baixo conforme a metade em que o cursor está, que é
 * a pergunta que o usuário faz enquanto arrasta.
 *
 * **O DOM é reordenado na hora, antes da resposta do servidor.** A lista é
 * markup simples — sem iframe, sem estado de cliente — então mover o nó é
 * seguro, e o render seguinte do LiveView confirma a mesma ordem. Esperar o
 * servidor deixaria um buraco visível entre soltar e ver.
 *
 * Diferente do `SortableCockpit`, que reordena por CSS `order` justamente
 * porque lá cada cartão tem um iframe que recarrega ao ser reparentado.
 */

const MARK = "data-sortable-over"

export default {
  mounted() {
    this._dragEl = null
    this._handleActive = false
    this._activeItem = null

    // Rede de segurança: solte onde soltar, o estado de arrasto morre aqui.
    this._onDocumentMouseUp = () => {
      this._handleActive = false
      if (this._activeItem) {
        this._activeItem.removeAttribute("draggable")
        this._activeItem = null
      }
      if (this._dragEl) {
        this._dragEl.style.opacity = ""
        this._dragEl = null
      }
      this._clearMarks()
    }
    document.addEventListener("mouseup", this._onDocumentMouseUp)

    this._bind()
  },

  updated() {
    this._bind()
  },

  destroyed() {
    if (this._onDocumentMouseUp) {
      document.removeEventListener("mouseup", this._onDocumentMouseUp)
      this._onDocumentMouseUp = null
    }
    this._clearMarks()
    this._dragEl = null
    this._activeItem = null
    this._handleActive = false
  },

  _items() {
    return [...this.el.querySelectorAll("[data-sortable-id]")]
  },

  _clearMarks() {
    this._items().forEach(item => item.removeAttribute(MARK))
  },

  _bind() {
    const self = this

    this._items().forEach(item => {
      if (item._sortableBound) return
      item._sortableBound = true

      const handle = item.querySelector("[data-drag-handle]")
      if (handle) {
        handle.addEventListener("mousedown", () => {
          self._handleActive = true
          self._activeItem = item
          item.setAttribute("draggable", "true")
        })
      }

      item.addEventListener("dragstart", e => {
        if (!self._handleActive) {
          // Arraste que não nasceu na alça (seleção de texto, arquivo vindo
          // de fora). Deixar seguir com dataTransfer vazio pode navegar a
          // página para lugar nenhum.
          e.preventDefault()
          return
        }
        self._dragEl = item
        item.style.opacity = "0.4"
        try {
          e.dataTransfer.effectAllowed = "move"
          e.dataTransfer.setData("text/plain", item.dataset.sortableId || "")
        } catch (_) {
          // Alguns navegadores recusam acesso ao dataTransfer em arraste
          // sintético; o movimento funciona sem o payload.
        }
      })

      item.addEventListener("dragend", () => {
        item.style.opacity = ""
        item.removeAttribute("draggable")
        self._handleActive = false
        self._activeItem = null
        self._dragEl = null
        self._clearMarks()
      })

      item.addEventListener("dragover", e => {
        if (!self._dragEl || item === self._dragEl) return
        e.preventDefault()
        try {
          e.dataTransfer.dropEffect = "move"
        } catch (_) {}

        const rect = item.getBoundingClientRect()
        const before = e.clientY < rect.top + rect.height / 2
        self._clearMarks()
        item.setAttribute(MARK, before ? "before" : "after")
      })

      item.addEventListener("dragleave", () => {
        item.removeAttribute(MARK)
      })

      item.addEventListener("drop", e => {
        e.preventDefault()
        const dragEl = self._dragEl
        const before = item.getAttribute(MARK) !== "after"
        self._clearMarks()

        if (!dragEl || dragEl === item || !document.contains(dragEl)) return

        if (before) {
          item.parentNode.insertBefore(dragEl, item)
        } else {
          item.parentNode.insertBefore(dragEl, item.nextSibling)
        }

        const order = self
          ._items()
          .map(el => el.dataset.sortableId)
          .filter(Boolean)

        const eventName = self.el.dataset.sortableEvent
        if (eventName && order.length > 0) {
          self.pushEventTo(self.el, eventName, { order })
        }
      })
    })
  }
}
